import React, { useState, useEffect, useMemo } from 'react';
import { Collapse, Tree } from 'antd';
import { 
  DownOutlined, 
  RightOutlined, 
  LeftOutlined, 
  CheckOutlined, 
  CloseOutlined
} from '@ant-design/icons';

const { Panel } = Collapse;

/**
 * DecisionTree component for displaying a hierarchical decision tree
 * 
 * @param {Object} props
 * @param {Object} props.data - The tree data structure
 * @param {string} props.title - The title of the accordion
 * @param {string} props.uuid - The UUID of the data object
 * @param {number} props.currentVersion - Current version of the data
 * @param {Object} props.versionInfo - Information about all versions
 * @param {Function} props.onVersionChange - Callback when version is changed
 * @param {Function} props.registerComponent - Function to register for updates
 */
const DecisionTree = ({
  data,
  title = "Decision Tree",
  uuid,
  currentVersion = 1,
  versionInfo = {},
  onVersionChange,
  registerComponent
}) => {
  // State to track whether the component is expanded
  const [isExpanded, setIsExpanded] = useState(true);
  
  // Handler for version navigation
  const handleVersionChange = (newVersion) => {
    if (onVersionChange && uuid) {
      onVersionChange(uuid, newVersion);
    }
  };
  
  // Component data sync from backend
  const [updateCounter, setUpdateCounter] = useState(0);

  // Register this component for updates if the function is provided
  useEffect(() => {
    if (registerComponent && uuid) {
      // Create a refresh function to update this component
      const refreshComponent = () => {
        console.log(`Refreshing Component: ${uuid}`);
        setUpdateCounter(prev => prev + 1);
      };
      
      // Register this component with the provided function
      const unregister = registerComponent(refreshComponent);
      
      // Unregister when the component unmounts
      return unregister;
    }
  }, [registerComponent, uuid]);

  // Custom bullet for nodes without result
  const renderBullet = () => (
    <span 
      style={{ 
        display: 'inline-block',
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        backgroundColor: '#6200EE', // Purple bullet to match theme
        marginRight: '8px',
        verticalAlign: 'middle'
      }}
    />
  );

  // Check if any node in the tree has a result property
  const hasAnyResult = useMemo(() => {
    // Recursive function to check for result property
    const checkForResult = (node) => {
      if (!node) return false;
      
      // If this node has a result property, return true
      if ('result' in node) return true;
      
      // Check children array
      if (node.children && Array.isArray(node.children)) {
        return node.children.some(child => checkForResult(child));
      }
      
      // Check left and right for backward compatibility
      if (node.left && checkForResult(node.left)) return true;
      if (node.right && checkForResult(node.right)) return true;
      
      return false;
    };
    
    return checkForResult(data);
  }, [data]);

  // Transform the tree data into antd Tree format
  const transformTreeData = (node) => {
    if (!node) return null;
    
    // Helper function to format node title
    const formatNodeTitle = (node) => {
      const opText = node.op === 'AND' ? 'All of the child conditions' : 
                     node.op === 'OR' ? 'Any of the child conditions' : '';
      
      // Format value if it exists
      const valueText = node.value !== undefined ? ` (${node.value})` : '';
      
      // Create the base title component with appropriate icon based on whether results exist in the tree
      let statusIcon;
      
      if ('result' in node) {
        // If this node has a result property, show the appropriate icon
        statusIcon = node.result ? 
          <CheckOutlined style={{ color: 'green', marginRight: '8px' }} /> : 
          <CloseOutlined style={{ color: 'red', marginRight: '8px' }} />;
      } else {
        // If no result, always show bullet
        statusIcon = renderBullet();
      }
      
      return (
        <span>
          {statusIcon}
          <span style={{ fontWeight: 'bold' }}>{node.text}{valueText}</span>
          {opText && <span style={{ color: '#666', marginLeft: '8px', fontSize: '100%' }}>
            - {opText}
          </span>}
        </span>
      );
    };
    
    // Process node based on type
    if (node.type === 'Leaf') {
      // Leaf node without children
      return {
        key: `node-${node.node_id}`,
        title: formatNodeTitle(node),
        isLeaf: true,
      };
    } else {
      // Process non-leaf nodes that might have children array
      let children = [];
      
      // Check for children array in the new format
      if (node.children && Array.isArray(node.children)) {
        children = node.children.map(child => transformTreeData(child)).filter(Boolean);
      } 
      // For backward compatibility, check for left and right properties
      else {
        if (node.left) {
          children.push(transformTreeData(node.left));
        }
        if (node.right) {
          children.push(transformTreeData(node.right));
        }
      }
      
      return {
        key: `node-${node.node_id}`,
        title: formatNodeTitle(node),
        children: children,
      };
    }
  };
  
  // Transform data into treeData format for antd Tree component
  const getTreeData = () => {
    if (!data) return [];
    
    // If data is already an array, map over it
    if (Array.isArray(data)) {
      return data.map(node => transformTreeData(node)).filter(Boolean);
    }
    
    // If data is an object (single root), wrap it in an array
    return [transformTreeData(data)].filter(Boolean);
  };
  
  // Check if version navigation should be displayed
  const showVersionNavigation = versionInfo && 
                               versionInfo.available_versions && 
                               versionInfo.available_versions.length > 1;
  
  // Show remarks if they exist in the data
  const hasRemarks = data && data.remarks;
  
  return (
    <div style={{ 
      marginBottom: '16px', 
      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
      borderRadius: '4px'
    }}>
      <Collapse
        defaultActiveKey={['1']}
        expandIcon={({ isActive }) => {
          setIsExpanded(isActive);
          return isActive ? <DownOutlined /> : <RightOutlined />;
        }}
        items={[
          {
            key: '1',
            label: (
              <div style={{ fontWeight: 'bold', fontSize: '18px' }}>
                {title}
              </div>
            ),
            children: (
              <div>
                {/* Remarks section if available */}
                {hasRemarks && (
                  <div style={{ 
                    padding: '12px',
                    marginBottom: '16px',
                    backgroundColor: '#fffbe6',
                    border: '1px solid #ffe58f',
                    borderRadius: '4px'
                  }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Notes:</div>
                    <div>{data.remarks}</div>
                  </div>
                )}
                
                {/* Icon legend - only show if results exist in the tree */}
                {hasAnyResult && (
                  <div style={{ 
                    display: 'flex', 
                    gap: '16px', 
                    marginBottom: '16px', 
                    padding: '8px',
                    background: '#f5f5f5',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}>
                    <div>
                      <CheckOutlined style={{ color: 'green', marginRight: '4px' }} />
                      <span>True condition</span>
                    </div>
                    <div>
                      <CloseOutlined style={{ color: 'red', marginRight: '4px' }} />
                      <span>False condition</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      {renderBullet()}
                      <span>Criteria node</span>
                    </div>
                  </div>
                )}
                
                {/* Tree Component */}
                <div className="tree-container" style={{ margin: '8px 0' }}>
                  <Tree
                    showLine={{ showLeafIcon: false }}
                    defaultExpandAll
                    switcherIcon={<DownOutlined />}
                    treeData={getTreeData()}
                  />
                </div>
                
                {/* Version navigation */}
                {showVersionNavigation && (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: '16px', 
                    paddingTop: '16px',
                    borderTop: '1px solid #f0f0f0'
                  }}>
                    <button 
                      size="small" 
                      icon={<LeftOutlined />}
                      disabled={currentVersion <= Math.min(...versionInfo.available_versions)}
                      onClick={() => {
                        const versions = versionInfo.available_versions;
                        const currentIndex = versions.indexOf(currentVersion);
                        if (currentIndex > 0) {
                          handleVersionChange(versions[currentIndex - 1]);
                        }
                      }}
                    >
                      Previous
                    </button>
                    
                    <div>
                      Version {currentVersion} of {versionInfo.total_versions}
                    </div>
                    
                    <button 
                      size="small" 
                      icon={<RightOutlined />}
                      disabled={currentVersion >= Math.max(...versionInfo.available_versions)}
                      onClick={() => {
                        const versions = versionInfo.available_versions;
                        const currentIndex = versions.indexOf(currentVersion);
                        if (currentIndex < versions.length - 1) {
                          handleVersionChange(versions[currentIndex + 1]);
                        }
                      }}
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            )
          }
        ]}
      />
    </div>
  );
};

export default DecisionTree;